import { useCallback, useEffect, useRef, useState } from 'react'
import { authRequest } from '../util/api/auth-api'
import useStore from '../util/store/auth'
import { Outlet } from 'react-router-dom'
import EmptyPageContainer from '../components/EmptyPageContainer'
import EmptyState from '../components/EmptyState'
import { RLicense } from '@counsel-project/counsel-auth-api'
import getUser from '../util/auth/getUser'
import useRequireAuth from '../util/auth/useRequireAuth'

const isValidAuditLicense = (license: RLicense) => {
  return license.auditProduct && license.enabled && new Date(license.expiresAt) > new Date()
}

/**
 * RequireDirectory is a wrapper component that ensures:
 * The directory is selected.
 * The user has a valid personal or directory audit license.
 */
const RequireDirectory = () => {
  const { directory, directoryLicense, setDirectoryLicense } = useStore()
  const user = getUser()
  const currentPage = globalThis.location.pathname + globalThis.location.search

  useRequireAuth(currentPage)

  const [isLoading, setIsLoading] = useState(true)

  const currentlyCheckingDir = useRef<string | null>(null)

  const populateLicense = useCallback(async () => {
    try {
      if (!directory) return

      setIsLoading(true)

      currentlyCheckingDir.current = directory._id

      const { licenses } = await authRequest.user.licenses.list({
        token: '',
        limit: 10,
        offset: 0,
        search: {
          and: [
            {
              directoryId: directory._id,
              userId: user?._id,
            },
          ],
        },
      })

      // early return if the directory has changed
      if (currentlyCheckingDir.current !== directory._id) return

      let foundLicense = licenses.find(isValidAuditLicense) ?? null

      if (!foundLicense) {
        // Get user roles and directory licenses in parallel
        const [userRolesRes, directoryLicensesRes] = await Promise.all([
          authRequest.user.directories.roles.list({
            token: '',
            directoryId: currentlyCheckingDir.current,
            search: {
              and: [{ userId: user?._id }],
            },
          }),
          authRequest.user.directories.licenses.list({
            token: '',
            directoryId: currentlyCheckingDir.current,
          }),
        ])

        const userRole = userRolesRes.results?.[0]
        const directoryLicenses = directoryLicensesRes.results ?? []

        const foundDirectoryLicense = userRole?.useAuditLicense
          ? (directoryLicenses.find(isValidAuditLicense) ?? null)
          : null

        foundLicense = foundDirectoryLicense
      }

      if (currentlyCheckingDir.current !== directory._id) return

      setDirectoryLicense(foundLicense)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [directory, setDirectoryLicense, user])

  useEffect(() => {
    const timeout = setTimeout(() => {
      void populateLicense()
    }, 10)

    return () => clearTimeout(timeout)
  }, [populateLicense])

  if (!user) {
    return null
  }

  if (!directory) {
    return (
      <EmptyPageContainer>
        <EmptyState
          emptyStateVariant="error"
          title="Please select an organization to view this page"
          description=""
        />
      </EmptyPageContainer>
    )
  }

  // Allow system admins to access the internal pages regardless of license
  if (user?.admin) {
    return <Outlet />
  }

  // Show loading state while checking license
  if (isLoading && !directoryLicense) {
    return (
      <EmptyPageContainer>
        <EmptyState
          emptyStateVariant="loading"
          title="Loading..."
          description="Loading the organization details"
        />
      </EmptyPageContainer>
    )
  }

  if (!directoryLicense?.auditProduct) {
    return (
      <EmptyPageContainer>
        <EmptyState
          emptyStateVariant="error"
          title="This organization does not have access to Comply"
          description=""
        />
      </EmptyPageContainer>
    )
  }

  return <Outlet />
}

export default RequireDirectory
